// The page's view (tab, dates, comparison, territory, salesman, focus) lives in
// the address bar, so a refresh or a shared link opens the same view.
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { previousPeriodComparison, resolveDateRange } from "./dateRanges";
import { dimensionOfKey } from "./drillDown";

dayjs.extend(utc);

export const TABS = ["summary", "customers", "products", "salesmen", "seasons"];
const TAB_ALIASES = { explore: "summary" };
export const RANGES = ["today", "yesterday", "this_week", "last_week", "mtd", "last_month", "ytd", "custom"];
export const COMPARES = ["previous_period", "previous_month", "previous_hebrew_year", "custom", "none"];
export const CUSTOMER_LISTS = ["attention", "late", "switching", "less", "lost", "new", "back", "all"];
const CUSTOMER_KEY = /^(customer|account):\d+$/;
const SALESMAN_KEY = /^salesman:(\d+|unassigned)$/;
const PRODUCT_KEY = /^product:\d+$/;
// The open details panel or breakdown; each belongs to one tab.
export const PANEL_KEYS = ["customer", "rep", "product", "by"];
export const BREAKDOWNS = ["product_category", "product", "customer", "route", "vendor", "account_type", "salesman"];
export const RANGE_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This week to date" },
  { value: "last_week", label: "Last full week" },
  { value: "mtd", label: "Month to date" },
  { value: "last_month", label: "Last full month" },
  { value: "ytd", label: "Year to date" },
  { value: "custom", label: "Custom dates" },
];
export const COMPARE_OPTIONS = [
  { value: "previous_period", label: "Previous period", hint: "same weekdays" },
  { value: "previous_month", label: "Same period last month", hint: "same weekdays" },
  { value: "previous_hebrew_year", label: "Same period last Hebrew year", hint: "nearest same weekday" },
  { value: "custom", label: "Custom dates" },
  { value: "none", label: "No comparison" },
];
const ISO = /^\d{4}-\d{2}-\d{2}$/;

export const DEFAULT_STATE = {
  tab: "summary",
  by: null,
  list: "attention",
  customer: null,
  rep: null,
  product: null,
  range: "mtd",
  from: null,
  to: null,
  compare: "previous_period",
  cfrom: null,
  cto: null,
  territory: null,
  territoryName: null,
  salesman: null,
  salesmanName: null,
  focus: [],
};

const validDate = (value) => (value && ISO.test(value) && dayjs.utc(value).isValid() ? value : null);

export function parseState(search) {
  const params = new URLSearchParams(search || "");
  const requestedTab = params.get("tab");
  const tab = TAB_ALIASES[requestedTab] || requestedTab;
  let range = RANGES.includes(params.get("range")) ? params.get("range") : DEFAULT_STATE.range;
  const from = validDate(params.get("from"));
  const to = validDate(params.get("to"));
  if (range === "custom" && (!from || !to || to < from)) range = DEFAULT_STATE.range;
  let compare = COMPARES.includes(params.get("compare")) ? params.get("compare") : DEFAULT_STATE.compare;
  const cfrom = validDate(params.get("cfrom"));
  const cto = validDate(params.get("cto"));
  if (compare === "custom" && (!cfrom || !cto || cto < cfrom)) compare = DEFAULT_STATE.compare;
  const territory = /^\d+$/.test(params.get("territory") || "") ? Number(params.get("territory")) : null;
  const rawSalesman = params.get("salesman");
  const salesman = rawSalesman === "unassigned" ? "unassigned" : /^\d+$/.test(rawSalesman || "") ? Number(rawSalesman) : null;
  const focus = params.getAll("focus").map((value) => {
    const [key, ...name] = value.split("|");
    const dimension = dimensionOfKey(key);
    return dimension && dimension !== "salesman" ? { key, dimension, name: name.join("|") || "Unassigned" } : null;
  }).filter(Boolean);
  return {
    tab: TABS.includes(tab) ? tab : DEFAULT_STATE.tab,
    by: BREAKDOWNS.includes(params.get("by")) ? params.get("by") : null,
    list: CUSTOMER_LISTS.includes(params.get("list")) ? params.get("list") : DEFAULT_STATE.list,
    customer: CUSTOMER_KEY.test(params.get("customer") || "") ? params.get("customer") : null,
    rep: SALESMAN_KEY.test(params.get("rep") || "") ? params.get("rep") : null,
    product: PRODUCT_KEY.test(params.get("product") || "") ? params.get("product") : null,
    range,
    from: range === "custom" ? from : null,
    to: range === "custom" ? to : null,
    compare,
    cfrom: compare === "custom" ? cfrom : null,
    cto: compare === "custom" ? cto : null,
    territory,
    territoryName: territory != null ? params.get("territory_name") || `Territory ${territory}` : null,
    salesman,
    salesmanName: salesman != null ? params.get("salesman_name") || (salesman === "unassigned" ? "Office / Unassigned" : `Salesman ${salesman}`) : null,
    focus: focus.filter((item, index) => focus.findIndex((other) => other.dimension === item.dimension) === index),
  };
}

export function serializeState(state) {
  const params = new URLSearchParams();
  if (state.tab !== DEFAULT_STATE.tab) params.set("tab", state.tab);
  if (state.by) params.set("by", state.by);
  if (state.list && state.list !== DEFAULT_STATE.list) params.set("list", state.list);
  if (state.customer) params.set("customer", state.customer);
  if (state.rep) params.set("rep", state.rep);
  if (state.product) params.set("product", state.product);
  if (state.range !== DEFAULT_STATE.range) params.set("range", state.range);
  if (state.range === "custom") { params.set("from", state.from); params.set("to", state.to); }
  if (state.compare !== DEFAULT_STATE.compare) params.set("compare", state.compare);
  if (state.compare === "custom") { params.set("cfrom", state.cfrom); params.set("cto", state.cto); }
  if (state.territory != null) {
    params.set("territory", state.territory);
    if (state.territoryName) params.set("territory_name", state.territoryName);
  }
  if (state.salesman != null) {
    params.set("salesman", state.salesman);
    if (state.salesmanName) params.set("salesman_name", state.salesmanName);
  }
  state.focus.forEach((item) => params.append("focus", `${item.key}|${item.name}`));
  return params.toString();
}

export function writeStateToAddress(state) {
  try {
    const url = new URL(window.location.href);
    url.search = serializeState(state);
    window.history.replaceState(window.history.state, "", url);
  } catch (error) {
    // The address bar is a convenience only.
  }
}

/** The dates actually selected, as YYYY-MM-DD strings. */
export function selectedDates(state, today) {
  if (state.range === "custom") return { start_date: state.from, end_date: state.to };
  const range = today ? resolveDateRange(state.range, today) : resolveDateRange(state.range);
  return { start_date: range.startDate.format("YYYY-MM-DD"), end_date: range.endDate.format("YYYY-MM-DD") };
}

/** Dates, comparison and territory sent to every report. */
export function buildRequest(state, today) {
  const dates = selectedDates(state, today);
  let comparison;
  if (state.compare === "previous_period") {
    comparison = previousPeriodComparison(dayjs.utc(dates.start_date), dayjs.utc(dates.end_date));
  } else if (state.compare === "custom") {
    comparison = { comparison: "custom", comparison_start_date: state.cfrom, comparison_end_date: state.cto };
  } else {
    comparison = { comparison: state.compare };
  }
  return { ...dates, ...comparison, ...(state.territory != null ? { territory_id: state.territory } : {}) };
}

/** Focus keys sent as `filter`, the salesman first. */
export function filterKeys(state) {
  return [...(state.salesman != null ? [`salesman:${state.salesman}`] : []), ...state.focus.map((item) => item.key)];
}
